/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { spacing } from '../../shared/theme/spacing'
import { ticketColors } from '../../shared/theme/ticketColors';
import { PaperSheet } from '../../shared/ticket/PaperSheet';
import type { ServiceRecord } from '../screens/hooks/useCustomerServiceRecords';

type Props = {
  records: ServiceRecord[];
  loading: boolean;
  mobile?: boolean;
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('es-AR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function CustomerServiceRecordsSection({
  records,
  loading,
  mobile = false,
}: Props) {
  const hasRecords = records.length > 0;

  return (
    <PaperSheet
      style={mobile ? styles.panelMobile : styles.panelWide}
      testID="customers.detail.service-records"
    >
      <View style={styles.headerRow}>
        <Ionicons name="document-text-outline" size={18} color={ticketColors.ink} />
        <Text style={styles.title}>Atención</Text>
      </View>

      {loading ? (
        <Text style={styles.emptyText}>Cargando atención…</Text>
      ) : !hasRecords ? (
        <Text style={styles.emptyText}>Sin registros de atención.</Text>
      ) : (
        <View style={styles.list}>
          {records.map((r) => (
            <View key={r.id} style={styles.recordCard} testID="customers.detail.service-record">
              <View style={styles.recordHeader}>
                <Text style={styles.recordDate}>{formatDate(r.visitDate)}</Text>
                {r.recommendedReturnDate ? (
                  <View style={styles.returnBadge}>
                    <Text style={styles.returnText}>
                      Volver: {formatDate(r.recommendedReturnDate)}
                    </Text>
                  </View>
                ) : null}
              </View>
              {r.observations ? (
                <Text style={styles.observations}>{r.observations}</Text>
              ) : null}
              {r.recommendedNextService ? (
                <Text style={styles.recommendation}>
                  Próximo: {r.recommendedNextService}
                </Text>
              ) : null}
              {r.items.length > 0 ? (
                <View style={styles.itemsList}>
                  {r.items.map((item) => (
                    <View key={item.id} style={styles.itemRow}>
                      <Text style={styles.itemLabel}>{item.fieldLabel}:</Text>
                      <Text style={styles.itemValue}>
                        {item.valueText ??
                          (item.valueNumber != null ? String(item.valueNumber) : '') ??
                          (item.valueDate ? formatDate(item.valueDate) : '')}
                      </Text>
                    </View>
                  ))}
                </View>
              ) : null}
            </View>
          ))}
        </View>
      )}
    </PaperSheet>
  );
}

const styles = StyleSheet.create({
  panelWide: {
    padding: spacing.md,
  },
  panelMobile: {
    padding: spacing.sm,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.sm,
  },
  title: {
    fontSize: 15,
    fontWeight: '600',
    color: ticketColors.ink,
  },
  list: {
    gap: spacing.sm,
  },
  recordCard: {
    paddingVertical: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
  },
  recordHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.xs,
  },
  recordDate: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink,
  },
  returnBadge: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 3,
    borderRadius: 12,
    backgroundColor: ticketColors.gold + '22',
  },
  returnText: {
    fontSize: 11,
    fontWeight: '600',
    color: ticketColors.goldInk,
  },
  observations: {
    fontSize: 14,
    color: ticketColors.ink2,
    marginBottom: spacing.xs,
  },
  recommendation: {
    fontSize: 13,
    color: ticketColors.ink3,
    fontStyle: 'italic',
    marginBottom: spacing.xs,
  },
  itemsList: {
    gap: 2,
  },
  itemRow: {
    flexDirection: 'row',
    gap: spacing.xs,
  },
  itemLabel: {
    fontSize: 12,
    color: ticketColors.ink3,
    fontWeight: '500',
  },
  itemValue: {
    fontSize: 12,
    color: ticketColors.ink2,
    flex: 1,
  },
  emptyText: {
    fontSize: 14,
    color: ticketColors.ink3,
    paddingVertical: spacing.sm,
  },
});
